
/**
 * @typedef {import('../model/signup-type').SignupType } SignupType
 */

/**
 * RegEx and Error pair for validating from field values
 * @property {string} regex Regular Expression for validating the field values
 * @property {string} error Error to display when validation fails
 */
class FormValidation {
    /** @constructor */
    constructor({
        regex,
        error
    }) {
        this.regex = regex;
        this.error = error;
    }
}

/**
 * FormFied configuration from AEM for the signup form.
 * @property {string} value The form field value
 * @property {string} mask The form field mask
 * @property {string} hint The form field hint
 * @property {string} itemText The form field itemText
 * @property {string} itemValue The form field itemValue
 * @property {FormFieldItem[]} items The form field items
 * @property {string} label The form field label
 * @property {boolean} required Indicates if the field value is required
 * @property {string} key The form field key
 * @property {number} maxLength The maximum allowable length
 * @property {string} inputType The kind of control used for the field
 * @property {string} type The javascript type of the data (e.g. string)
 * @property {FormValidation[]} validation The regex and error pairs for validating the value of the field
 */
class FormField {
    /** @constructor */
    constructor({
        value,
        mask,
        hint,
        itemText,
        itemValue,
        items,
        label,
        required,
        key,
        minLength,
        maxLength,
        inputType,
        type,
        validation
    }) {
        this.value = value;
        this.mask = mask;
        this.hint = hint;
        this.itemText = itemText;
        this.itemValue = itemValue;
        this.items = [];
        if (items) {
            items.forEach((item) => {
                if (!(item instanceof FormFieldItem)) {
                    this.items.push(new FormFieldItem(item));
                } else {
                    this.items.push(item);
                }
            });
        };
        this.label = label;
        this.required = required;
        this.key = key;
        this.maxLength = maxLength;
        this.minLength = minLength;
        this.inputType = inputType;
        this.type = type;
        this.validation = [];
        if (validation) {
            validation.forEach((validationItem) => {
                if (!(validationItem instanceof FormValidation)) {
                    this.validation.push(new FormValidation(validationItem));
                } else {
                    this.validation.push(validationItem);
                }
            });
        }
    }
}

/**
 * An object representing formFieldItem
 * @property {string} name name to be returned to the client
 * @property {string} value value to be reeturned to the client
 */
class FormFieldItem {
    /** @constructor */
    constructor({
        name,
        value
    }) {
        this.name = name;
        this.value = value;
    }
}
/**
 * The list of all the field definitions for the signup form
 * @property {FormField[]} fields The actual list of fields for the signup form
 * @property {Object[]} grids grid information
 */
export default class SignupSchema {
    /** @constructor */
    constructor({
        fields,
        grids
    }) {
        this.fields = [];
        if (fields) {
            fields.forEach((field) => {
                if (!(field instanceof FormField)) {
                    this.fields.push(new FormField(field));
                } else {
                    this.fields.push(field);
                }
            });
        }
        this.grids = grids;
    }
}

export {
    FormField,
    FormFieldItem,
    FormValidation
};
